import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Info, Search, UserPlus } from 'lucide-react';
import { ADMIN } from '@noissue/authz';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
  notify,
} from '@noissue/os-ui';
import { ApiError, meQueryOptions } from '../api';
import { adminApi } from './admin-api';
import { AccessSummary } from './admin-page';
import { ACCESS_CONTROL_PATH, groupPath, USERS_PATH, userPath } from './paths';

/**
 * Users. The list and each person's access are READ-ONLY — it is what enforces "access
 * comes from groups", and it answers the question the old users × modules matrix could
 * not, namely which group is responsible for each permission. To change what someone can
 * do, change their groups.
 *
 * The one write here is ADDING a person, which is not a change to anyone's access: it
 * brings the account into existence so the group screens have something to point at
 * before that person has ever signed in.
 */
export function UsersPage() {
  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: adminApi.users });
  const me = useQuery(meQueryOptions);
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);

  // The server guards the route regardless; this only decides whether to offer a button
  // that would 403.
  const canAddUsers = me.data?.permissions.includes(ADMIN.p.manageUsers) ?? false;

  const term = query.trim().toLowerCase();
  const filtered = (users.data ?? []).filter(
    (u) =>
      term === '' ||
      `${u.name} ${u.email} ${u.groups.map((g) => g.name).join(' ')}`.toLowerCase().includes(term),
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground">
            Who can do what, and which group grants it. To change someone&rsquo;s access, edit
            their groups in <Link to={ACCESS_CONTROL_PATH} className="underline">Access control</Link>.
          </p>
        </div>
        {canAddUsers && (
          <Button onClick={() => setAdding(true)}>
            <UserPlus className="size-4" />
            Add person
          </Button>
        )}
      </div>

      <NewUserDialog
        open={adding}
        onOpenChange={setAdding}
      />

      <div className="relative max-w-md">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9"
          placeholder="Search by name, email or group"
          aria-label="Search users"
        />
      </div>

      {users.isPending && <Skeleton className="h-64 w-full" />}
      {users.isError && (
        <Card className="p-6 text-sm text-destructive">
          Couldn&rsquo;t load users. {(users.error as Error).message}
        </Card>
      )}

      {users.data && filtered.length === 0 && (
        <Card className="py-12 text-center text-sm text-muted-foreground">
          No one matches that search.
        </Card>
      )}

      <div className="divide-y rounded-md border bg-card">
        {filtered.map((u) => (
          <Link
            key={u.id}
            to={userPath(u.id)}
            className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-accent/40"
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{u.name}</div>
              <div className="truncate text-xs text-muted-foreground">{u.email}</div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {u.groups.map((g) => (
                <Badge key={g.id} variant="secondary" className="font-normal">
                  {g.name}
                </Badge>
              ))}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

/**
 * Add someone before their first sign-in.
 *
 * Three fields and nothing else, because that is all the account is: no password is set
 * and none can be — sign-in is still Google, and this row is what that sign-in lands on.
 * The dialog says so, since "add a user" otherwise reads like it might send an invitation.
 */
function NewUserDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const reset = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
  };

  const create = useMutation({
    mutationFn: () =>
      adminApi.createUser({
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      }),
    onSuccess: (created) => {
      notify.success(`Added ${created.name}. Put them in their groups next.`);
      reset();
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    // The server's messages are the useful ones — a wrong domain, or an email that is
    // already someone's account, each say exactly which. Surface them verbatim.
    onError: (e) => notify.error(e instanceof ApiError ? e.message : 'Could not add them.'),
  });

  const ready = email.trim() !== '' && firstName.trim() !== '' && lastName.trim() !== '';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a person</DialogTitle>
          <DialogDescription>
            Creates their account so you can put them in groups now, rather than waiting for
            them to sign in first. They still sign in with Google — this sends nothing and
            sets no password.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-user-email">Work email</Label>
            <Input
              id="new-user-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="firstname@noissue.co"
              autoComplete="off"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="new-user-first">First name</Label>
              <Input
                id="new-user-first"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-user-last">Last name</Label>
              <Input
                id="new-user-last"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={!ready || create.isPending}>
            {create.isPending ? 'Adding…' : 'Add person'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One person's effective access, with the group responsible for each module. */
export function UserAccessPage() {
  const { userId = '' } = useParams({ strict: false }) as { userId?: string };
  const person = useQuery({
    queryKey: ['admin', 'user-access', userId],
    queryFn: () => adminApi.userAccess(userId),
  });

  if (person.isPending) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (person.isError || !person.data) {
    return (
      <div className="p-6">
        <Card className="p-6 text-sm text-destructive">
          Couldn&rsquo;t load this person. {(person.error as Error | null)?.message}
        </Card>
      </div>
    );
  }

  const p = person.data;

  return (
    <div className="space-y-6 p-6">
      <div>
        <Link
          to={USERS_PATH}
          className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" /> Users
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">{p.name}</h1>
        <p className="text-sm text-muted-foreground">{p.email}</p>
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold">Groups</h2>
        {p.groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">No groups.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {p.groups.map((g) => (
              <Link key={g.id} to={groupPath(g.id)}>
                <Badge variant="secondary" className="font-normal hover:bg-accent">
                  {g.name}
                </Badge>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold">Effective access</h2>
        {p.access.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No module access. They can sign in, but will see nothing.
          </p>
        ) : (
          <div className="divide-y rounded-md border bg-card">
            {p.access.map((a) => (
              <div key={a.moduleId} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div>
                  <div className="text-sm font-medium">{a.moduleName}</div>
                  <div className="text-xs text-muted-foreground">
                    via{' '}
                    {a.viaGroups.map((g, i) => (
                      <span key={g.id}>
                        {i > 0 && ', '}
                        <Link to={groupPath(g.id)} className="underline">
                          {g.name}
                        </Link>
                      </span>
                    ))}
                  </div>
                </div>
                <AccessSummary access={[a]} />
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold">Permissions</h2>
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 size-3.5 shrink-0" />
          The resolved strings the API checks. Levels expand to these when a group&rsquo;s
          access is saved.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {p.permissions.map((perm) => (
            <code key={perm} className="rounded bg-muted px-1.5 py-0.5 text-xs">
              {perm}
            </code>
          ))}
        </div>
      </section>
    </div>
  );
}
